import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SwitchCustom from './inputs/SwitchCustom';
import { Box, FormLabel, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import CurrencyInput from './inputs/CurrencyInput';
import NumberInput from './inputs/NumberInput';
import PaginationSection from './PaginationSection';
import ShowLoading from './ShowLoading';


const tableHeaderStyle = {
    backgroundColor: "#A1A1A1",
    fontSize: "1rem",
    color: "white",
    borderLeft: "1px solid white",
    borderRight: "1px solid white",
    padding: "8px",
    minWidth: "10rem",
    zIndex: 1000
}

const tableSectionHeaderStyle = {
    backgroundColor: "#818181",
    fontSize: "1rem",
    border: "1px solid #A1A1A1",
    color: "white",
    padding: "8px"
};

const tableActionsCellStyle = {
    backgroundColor: '#fc561f48',
    minWidth: "22rem"
};

const tableValueRowStyle = {
    color: "black",
    fontWeight: "bold",
    fontSize: "1rem",
    border: "1px solid #A1A1A1",
    padding: "8px",
    minWidth: "22rem"
};


function CharacteristicDependencyValuesTable({
    itemToEdit,
    primaryCharacteristicName,
    primaryCharacteristicValues,
    characteristicActions,
    variantMatrix,
    handleUpdatePrimary,
    handleApplyToAllPrimaryValues,
    sharedPriceModifier,
    setSharedPriceModifier,
    sharedWeightModifier,
    setSharedWeightModifier,
    setSharedDepth,
    sharedDepth,
    setSharedWidth,
    sharedWidth,
    setSharedHeight,
    sharedHeight,
    characteristics,
    dependencyForm,
    updateMatrixCell,
    handleMatrixAction,
}) {

    const [ page, setPage ] = useState(1);
    const [ colsPerPage, setColsPerPage ] = useState(2);
    const [ totalPages, setTotalPages ] = useState(0);

    const [isLoading, setIsLoading] = useState(false);

    const [columns, setColumns] = useState([]);

    useEffect(() => {
        setTotalPages(Math.ceil(primaryCharacteristicValues.length / colsPerPage));
    }, [primaryCharacteristicValues, colsPerPage]);

    const onPageChange = (newPage) => {
        setPage(newPage);
    }

    const onColsPerPageChange  = (newColsPerPage) => {
        setColsPerPage(newColsPerPage);
        setPage(1);

        setTotalPages(Math.ceil(primaryCharacteristicValues.length / newColsPerPage));
    }

    const MatrixCell = memo(function MatrixCell({
        cellData,
        char, 
        onCommit
    }) {
        const [ localValue, setLocalValue ] = useState(cellData);
        const isEditingRef = useRef(false);

        const timeoutRef = useRef();

        // Update local value when cellData changes
        useEffect(() => {
            // console.log("CHANGING CELL DATA")
            setLocalValue(cellData);
        }, [cellData]);

        const applyChanges = useCallback((updated) => {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                // console.log("APPLYING CHANGES")
                onCommit(updated);
            }, 750);
        }, [onCommit]);

        const handleChange = (field, value) => {
            // console.log("CHANGING VALUE")

            setLocalValue(prev => {
                const updated = {
                    ...prev,
                    [field]: value
                };
                onCommit(updated); // pass the updated object!
                return updated;
            });
        }

        return (
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: 2 }}>
                <SwitchCustom
                    checked={localValue?.enabled || false} 
                    onChange={(e) => {
                        handleChange(
                            "enabled",
                            e.target.checked
                        ); 
                    }}
                />

                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 2, width: "100%" }}>
                    <CurrencyInput
                        value={localValue?.price_modifier || 0}
                        onCommit={(e) => {
                            handleChange(
                                "price_modifier",
                                parseFloat(e.target.value) || 0
                            )
                        }}
                        disabled={
                            !(
                            char.characteristicCategory === "price_only" ||
                            char.characteristicCategory === "price_and_dimensions"
                            )
                        }
                    />
                    {
                        (char.characteristicCategory === "dimensions_only" ||
                        char.characteristicCategory === "price_and_dimensions") && (
                            <>
                                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: 2 }}>
                                    <NumberInput 
                                        label="Largo" 
                                        value={localValue?.depth || 0} 
                                        unit="cm" 
                                        onCommit={(e) => {
                                            handleChange(
                                                "depth",
                                                parseFloat(e.target.value) || 0
                                            )
                                        }}
                                    />
                                    <NumberInput 
                                        label="Ancho" 
                                        value={localValue?.width || 0} 
                                        unit="cm" 
                                        onCommit={(e) => {
                                            handleChange(
                                                "width",
                                                parseFloat(e.target.value) || 0
                                            )
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: 2 }}>
                                    <NumberInput 
                                        label="Alto" 
                                        value={localValue?.height || 0} 
                                        unit="cm" 
                                        onCommit={(e) => {
                                            handleChange(
                                                "height",
                                                parseFloat(e.target.value) || 0
                                            )
                                        }}
                                    />
                                    <NumberInput 
                                        label="Peso" 
                                        value={localValue?.weight_modifier || 0} 
                                        unit="kg" 
                                        onCommit={(e) => {
                                            handleChange(
                                                "weight_modifier",
                                                parseFloat(e.target.value) || 0
                                            )
                                        }} 
                                    />
                                </Box>
                            </>
                        )
                    }
                </Box>
            </Box>
        )
    })

    const renderMatrixCells = (matrixValueIndex, charIndex, valIndex) => {
        matrixValueIndex = (page - 1) * colsPerPage + matrixValueIndex;

        const cellData = variantMatrix[matrixValueIndex]?.characteristics?.[charIndex]?.values?.[valIndex];
        const char = variantMatrix[matrixValueIndex]?.characteristics?.[charIndex]

        return (
            <MatrixCell
                key={`${matrixValueIndex}-${charIndex}-${valIndex}`}
                cellData={cellData}
                char={char}
                onCommit={(updatedCell) => {
                    // commit all updated fields
                    Object.entries(updatedCell).forEach(([field, val]) => {
                    updateMatrixCell(val, matrixValueIndex, charIndex, valIndex, field);
                    });
                }}
            />
        )
    }

    const renderActionCell = (charIndex, valIndex) => {
        const cellData = characteristicActions?.[charIndex]?.values?.[valIndex];
        const char = characteristicActions[charIndex]

        return (
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <SwitchCustom 
                    checked={cellData?.enabled || false} 
                    onChange={(e) => {
                        handleMatrixAction(e, charIndex, valIndex, "enabled"); 
                    }}
                />
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 2, width: "100%" }}>
                    <CurrencyInput
                        value={cellData?.price_modifier || 0}
                        onCommit={(e) => {
                            handleMatrixAction(e, charIndex, valIndex, "price_modifier"); 
                        }}
                        disabled={
                            !(
                            char.characteristicCategory === "price_only" ||
                            char.characteristicCategory === "price_and_dimensions"
                            )
                        }
                    />
                    {
                        (char.characteristicCategory === "dimensions_only" ||
                        char.characteristicCategory === "price_and_dimensions") && (
                            <>
                                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: 2 }}>
                                    <NumberInput 
                                        label="Largo" 
                                        value={cellData?.depth || 0} 
                                        unit="cm" 
                                        onCommit={(e) => {
                                            handleMatrixAction(
                                                e,
                                                charIndex,
                                                valIndex,
                                                "depth"
                                            ); 
                                        }} 
                                    />
                                    <NumberInput 
                                        label="Ancho" 
                                        value={cellData?.width || 0} 
                                        unit="cm" 
                                        onCommit={(e) => {
                                            handleMatrixAction(
                                                e,
                                                charIndex,
                                                valIndex,
                                                "width"
                                            ); 
                                        }} 
                                    />
                                    </Box>
                                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: 2 }}>
                                    <NumberInput 
                                        label="Alto" 
                                        value={cellData?.height || 0} 
                                        unit="cm" 
                                        onCommit={(e) => {
                                            handleMatrixAction(
                                                e,
                                                charIndex,
                                                valIndex,
                                                "height"
                                            ); 
                                        }} 
                                    />
                                    <NumberInput 
                                        label="Peso" 
                                        value={cellData?.weight_modifier || 0} 
                                        unit="kg" 
                                        onCommit={(e) => {
                                            handleMatrixAction(
                                                e, 
                                                charIndex, 
                                                valIndex, 
                                                "weight_modifier"
                                            ); 
                                        }} 
                                    />
                                </Box>
                            </>
                        )
                    }
                </Box>
            </Box>
        )
    }

    const renderTable = () => {
        return (
            <>
                <PaginationSection
                    rows={columns}
                    page={page}
                    rowsPerPage={colsPerPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onColsPerPageChange}
                    isLoading={isLoading}
                    options={[2, 4, 6, 8, 10]}
                />
                
                <Box
                    sx={{ 
                        overflow: "auto",
                        height: "100%",
                    }}
                    component={Paper}
                >
                    <TableContainer sx={{ height: "80vh" }}>
                        <Table stickyHeader sx={{ borderCollapse: "collapse", width: "100%" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...tableHeaderStyle }} align="center">
                                        {primaryCharacteristicName}
                                    </TableCell>
                                    <TableCell sx={{ ...tableHeaderStyle, backgroundColor: '#FC581F', color: "white" }} align="center">
                                        ACCIONES
                                    </TableCell>
                                    {primaryCharacteristicValues.slice((page - 1) * colsPerPage, page * colsPerPage).map((item, index) => (
                                        <TableCell key={index} sx={{ ...tableHeaderStyle }} align="left">
                                            {item.valueName}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>
                                        Precio Base
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            ...tableActionsCellStyle,
                                            display: "flex", 
                                            flexDirection: "row", 
                                            justifyContent: "space-between",
                                            width: "100%"
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: "100%"
                                            }}
                                        >
                                            <CurrencyInput value={dependencyForm.base_price} disabled={true} />
                                        </Box>
                                    </TableCell>
                                    
                                    {primaryCharacteristicValues.slice((page - 1) * colsPerPage, page * colsPerPage).map((_, index) => (
                                        <TableCell key={index} sx={{ ...tableValueRowStyle }} align="left">
                                            <CurrencyInput 
                                                value={dependencyForm.base_price} 
                                                disabled={true} 
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Precio Extra por Característica
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            ...tableActionsCellStyle,
                                            display: "flex", 
                                            flexDirection: "row", 
                                            justifyContent: "space-between",
                                            width: "100%"
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: "100%"
                                            }}
                                        >
                                            <CurrencyInput 
                                                value={sharedPriceModifier} 
                                                onCommit={(e) => {
                                                    const val = parseFloat(e.target.value || 0.0)
                                                    setSharedPriceModifier(val)
                                                    handleApplyToAllPrimaryValues('price_modifier', val)
                                                }} 
                                            />
                                        </Box>
                                    </TableCell>

                                    {primaryCharacteristicValues.slice((page - 1) * colsPerPage, page * colsPerPage).map((item, index) => (
                                        <TableCell key={index} sx={{ ...tableValueRowStyle }} align="left">
                                            <CurrencyInput 
                                                value={item.price_modifier} 
                                                onCommit={(e) => {
                                                    const val = parseFloat(e.target.value || 0.0)
                                                    handleUpdatePrimary(
                                                        item.valueName, 
                                                        "price_modifier", 
                                                        val
                                                    )
                                                }}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Peso Base por Característica
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            ...tableActionsCellStyle,
                                            display: "flex", 
                                            flexDirection: "row", 
                                            justifyContent: "space-between",
                                            height: "100%",
                                            width: "100%"
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: "100%"
                                            }}
                                        >
                                            <NumberInput 
                                                value={sharedWeightModifier} 
                                                unit={"kg"} 
                                                onCommit={(e) => {
                                                    const val = parseFloat(e.target.value || 0)
                                                    setSharedWeightModifier(val)
                                                    handleApplyToAllPrimaryValues('weight_modifier', val)
                                                }} 
                                            />
                                        </Box>
                                    </TableCell>

                                    {primaryCharacteristicValues.slice((page - 1) * colsPerPage, page * colsPerPage).map((item, index) => (
                                        <TableCell key={index} sx={{ ...tableValueRowStyle }} align="left">
                                            <NumberInput 
                                                value={item.weight_modifier} 
                                                unit={"kg"} 
                                                onCommit={(e) => {
                                                    handleUpdatePrimary(
                                                        item.valueName, 
                                                        "weight_modifier", 
                                                        parseFloat(e.target.value || 0.0)
                                                    )
                                                }}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Dimensiones Base por Característica
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            ...tableActionsCellStyle,
                                            display: "flex", 
                                            flexDirection: "row", 
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                width: "100%"
                                            }}
                                        >
                                            <NumberInput 
                                                value={sharedDepth} 
                                                label="Largo"
                                                unit={"cm"}
                                                onCommit={(e) => {
                                                    const val = parseFloat(e.target.value || 0)
                                                    setSharedDepth(val)
                                                    handleApplyToAllPrimaryValues('depth', val)
                                                }}
                                            />
                                            <NumberInput 
                                                value={sharedWidth} 
                                                label="Ancho"
                                                unit={"cm"}
                                                onChange={(e) => {
                                                    setSharedWidth(
                                                        parseFloat(e.target.value || 0)
                                                    )
                                                }}
                                                onBlur={() => {
                                                    handleApplyToAllPrimaryValues(
                                                        'width', 
                                                        sharedWidth || 0
                                                    )
                                                }} 
                                            />
                                            <NumberInput 
                                                value={sharedHeight} 
                                                label="Alto"
                                                unit={"cm"}
                                                onCommit={(e) => {
                                                    const val = parseFloat(e.target.value || 0)
                                                    setSharedHeight(val)
                                                    handleApplyToAllPrimaryValues('height', val)
                                                }}
                                            />
                                        </Box>
                                    </TableCell>
                                    {primaryCharacteristicValues.slice((page - 1) * colsPerPage, page * colsPerPage).map((item, index) => (
                                        <TableCell key={index} sx={{ ...tableValueRowStyle }} align="left">
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <Box>
                                                    <FormLabel>Largo</FormLabel>
                                                    <NumberInput 
                                                        value={item.depth} 
                                                        unit={"cm"}
                                                        onCommit={(e) => {
                                                            handleUpdatePrimary(
                                                                item.valueName, 
                                                                "depth", 
                                                                parseFloat(e.target.value || 0)
                                                            )
                                                        }}
                                                    />
                                                </Box>
                                                <Box>
                                                    <FormLabel>Ancho</FormLabel>
                                                    <NumberInput 
                                                        value={item.width} 
                                                        unit={"cm"}
                                                        onCommit={(e) => {
                                                            handleUpdatePrimary(
                                                                item.valueName, 
                                                                "width", 
                                                                parseFloat(e.target.value || 0.0)
                                                            )
                                                        }}
                                                    />
                                                </Box>
                                                <Box>
                                                    <FormLabel>Alto</FormLabel>
                                                    <NumberInput 
                                                        value={item.height} 
                                                        unit={"cm"}
                                                        onCommit={(e) => {
                                                            handleUpdatePrimary(
                                                                item.valueName, 
                                                                "height", 
                                                                parseFloat(e.target.value || 0.0)
                                                            )
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </TableCell>
                                    ))}
                                </TableRow>

                                {characteristics && characteristics.map((char, charIndex) => (
                                    <React.Fragment key={charIndex}>
                                        <TableRow>
                                            <TableCell
                                                colSpan={(variantMatrix.length || 0) + 2}
                                                sx={{ ...tableSectionHeaderStyle }}
                                                align="left"
                                            >
                                                {char.characteristicName}
                                            </TableCell>

                                        </TableRow>
                                        {char.values.map((val, valIndex) => (
                                            <TableRow key={valIndex}>
                                                <TableCell>{val.valueName}</TableCell>

                                                <TableCell 
                                                    sx={{ 
                                                        ...tableActionsCellStyle,
                                                        display: "flex", 
                                                        flexDirection: "row", 
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    {renderActionCell(charIndex, valIndex)}
                                                </TableCell>

                                                {variantMatrix.slice((page - 1) * colsPerPage, page * colsPerPage).map((_, varIndex) => (
                                                    <TableCell 
                                                        key={`${charIndex}-${varIndex}`}
                                                        sx={{ ...tableValueRowStyle }}
                                                    >
                                                        {renderMatrixCells(varIndex, charIndex, valIndex)}
                                                    </TableCell>
                                                ))}

                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <PaginationSection
                    rows={columns}
                    page={page}
                    rowsPerPage={colsPerPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onColsPerPageChange}
                    isLoading={isLoading}
                    options={[2, 4, 6, 8, 10]}
                />
            </>
        )
    }
    
    return (
        <>
            {
                isLoading 
                ? <ShowLoading /> 
                : renderTable()
            }
        </>
    )
}

export default CharacteristicDependencyValuesTable